import { FC, Fragment, useState } from "react";
import { Collapse, IconButton, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useIntl } from "react-intl";
import { v4 } from "uuid";

import { useApiCall } from "@framework/react-hooks";

import { useFormatDateHumanReadable } from "../../../../../../../../hooks";
import { ITrainArrangementActionResponse, ITrainArrangementActionRow, ITrainArrangementRow } from "../../interfaces";
import { StyledHeaderTableCell, StyledTableCell } from "../styled";

interface ITranArrangementActionProps {
  row: ITrainArrangementRow;
  setIsLoading: (value: boolean) => void;
}

export const TrainArrangementRow: FC<ITranArrangementActionProps> = props => {
  const { row, setIsLoading } = props;
  const [open, setOpen] = useState(false);
  const [actions, setActions] = useState<ITrainArrangementActionRow[] | null>(null);

  const { formatMessage } = useIntl();
  const formatDate = useFormatDateHumanReadable();

  const transformActions = (json: ITrainArrangementActionResponse[]): ITrainArrangementActionRow[] =>
    json.map(item => {
      const { StartDate, EndDate, Name, Object, Description } = item;

      return {
        id: v4(),
        name: Name,
        endDate: formatDate(EndDate),
        startDate: formatDate(StartDate),
        description: Description,
        object: Object,
      };
    });

  const { fn: getActions } = useApiCall(
    (api, idTrainArrangement: number) => {
      setIsLoading(true);
      return api
        .fetchJson({
          url: `/train-arrangement/${idTrainArrangement}/action`,
          method: "GET",
        })
        .then((json: ITrainArrangementActionResponse[]) => {
          setActions(transformActions(json));
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    { success: false },
  );

  const handleGetActions = async () => {
    if (actions === null) {
      await getActions(undefined, row.id);
    }
    setOpen(prev => !prev);
  };

  return (
    <Fragment>
      <TableRow
        sx={{
          "& .MuiTableCell-root": {
            background: "rgba(250, 250, 255, 0.6)",
            borderBottom: "1px solid #EBEBF7",
            borderRight: "1px solid #EBEBF7",
            borderColor: "#EBEBF7",
            "&:last-of-type": {
              borderRight: "none",
            },
          },
        }}
      >
        <StyledHeaderTableCell
          width={48}
          sx={{
            maxWidth: 48,
            minWidth: 48,
            width: 48,
            p: 0,
            textAlign: "center",
            verticalAlign: "center",
            borderBottom: "1px solid #EBEBF7",
          }}
          align="center"
        >
          {row.actionsExists ? (
            <IconButton aria-label="expand row" size="small" onClick={handleGetActions}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          ) : null}
        </StyledHeaderTableCell>
        <StyledHeaderTableCell sx={{ borderColor: "#EBEBF7", width: "15%" }} component="th" scope="row">
          <Typography fontSize="inherit" fontWeight={500} lineHeight="16px" color="#363846">
            {row.startDate}
          </Typography>
        </StyledHeaderTableCell>
        <StyledHeaderTableCell sx={{ borderColor: "#EBEBF7", width: "15%" }}>
          <Typography fontSize="inherit" fontWeight={500} lineHeight="16px" color="#363846">
            {row.endDate}
          </Typography>
        </StyledHeaderTableCell>
        <StyledHeaderTableCell sx={{ borderColor: "#EBEBF7", width: "15%" }}>
          <Typography fontSize="inherit" fontWeight={500} lineHeight="16px" color="#363846">
            {formatMessage({ id: `enums.trainArrangementType.${row.name}` })}
          </Typography>
        </StyledHeaderTableCell>
        <StyledHeaderTableCell sx={{ borderColor: "#EBEBF7", width: "15%" }}>
          <Typography fontSize="inherit" fontWeight={500} lineHeight="16px" color="#363846">
            -
          </Typography>
        </StyledHeaderTableCell>
        <StyledHeaderTableCell sx={{ borderColor: "#EBEBF7", width: "15%" }}>
          <Typography fontSize="inherit" fontWeight={500} lineHeight="16px" color="#363846">
            {row.description}
          </Typography>
        </StyledHeaderTableCell>
        <StyledHeaderTableCell width={48} sx={{ borderColor: "#EBEBF7", borderRight: "none" }} />
      </TableRow>
      {actions?.length ? (
        <TableRow>
          <TableCell style={{ borderColor: "#EBEBF7", padding: 0, borderBottom: "none" }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Table aria-label="components" sx={{ tableLayout: "fixed" }}>
                <TableBody>
                  {actions.map(({ id, startDate, endDate, name, description, object }) => (
                    <TableRow
                      key={id}
                      sx={{
                        "& > *": { borderColor: "#EBEBF7", borderRight: "1px solid #EBEBF7" },
                      }}
                    >
                      <StyledTableCell width={48} sx={{ p: 0, m: 0 }} />
                      <StyledTableCell sx={{ width: "15%" }}>{startDate}</StyledTableCell>
                      <StyledTableCell sx={{ width: "15%" }}>{endDate}</StyledTableCell>
                      <StyledTableCell sx={{ width: "15%" }}>{name}</StyledTableCell>
                      <StyledTableCell sx={{ width: "15%" }}>{object}</StyledTableCell>
                      <StyledTableCell sx={{ width: "15%" }}>{description}</StyledTableCell>
                      <StyledTableCell width={48} sx={{ width: 48, borderRight: 0 }} />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
    </Fragment>
  );
};
